import ConfirmModal from "PFComponents/confirm/confirm_modal";
import CustomValuesList from "PFComponents/custom_values_list/custom_values_list";
import { DoubleSectionPill } from "PFComponents/double_section_pill/double_section_pill";
import AutoSelect from "PFComponents/select/autoselect";
import { Typography } from "PFComponents/typography";
import { fetchSkillsFrameworksOptions } from "PFCore/services/skills_frameworks/fetch_skills_frameworks_options";
import { Experience, SkillsFramework } from "PFTypes";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import css from "./skills_framework_select_modal.module.scss";
import { useSkillsFrameworkModal } from "./use_skills_framework_select_modal";

type SkillsFrameworksSelectModalProps = {
  skillsFramework?: SkillsFramework | null;
  onSubmit: (item: SkillsFramework) => void;
  onClose: () => void;
};

type ItemProps = {
  label?: string;
  children: ReactNode;
};

const Section = ({ children, label }: ItemProps) => (
  <div className={css.item}>
    {label && <Typography variant="labelBold">{label}</Typography>}
    <div className={css.skills}>{children}</div>
  </div>
);

export const SkillsFrameworksSelectModal = ({
  skillsFramework,
  onSubmit,
  onClose
}: SkillsFrameworksSelectModalProps) => {
  const { t } = useTranslation(["skillsFrameworks", "core"]);

  // TODO: [PROF-999] connect experience from api, now defaulted to intermediate on ui side
  const experienceTextMap = {
    [Experience.Basic]: t("core:skillRatings.basic"),
    [Experience.Intermediate]: t("core:skillRatings.intermediate"),
    [Experience.Advanced]: t("core:skillRatings.advanced")
  };
  const getExperienceText = (experience: Experience) =>
    experienceTextMap[experience] || experienceTextMap[Experience.Intermediate];

  const {
    essentialSkills,
    supportingSkills,
    customTypesWithValues,
    selectedFramework,
    setSelectedFramework
  } = useSkillsFrameworkModal(skillsFramework);

  const handleOK = selectedFramework
    ? () => {
        onSubmit(selectedFramework);
      }
    : undefined;

  return (
    <ConfirmModal
      title={t("skillsFrameworks:selectModal.skillsFramework")}
      handleOK={handleOK}
      handleClose={onClose}
      labelOK={t("skillsFrameworks:selectModal.apply")}
      classes={{
        dialog: css.modalDialog,
        title: css.title,
        modal: css.modal
      }}
    >
      <div className={css.root}>
        <AutoSelect
          values={selectedFramework ? [selectedFramework] : []}
          label={t("skillsFrameworks:selectModal.selectFramework")}
          query={(term) => fetchSkillsFrameworksOptions(term ? { term } : {})}
          formatOption={(item) => ({
            id: item.id,
            displayElement: item.name,
            item: {
              ...item,
              text: item.name
            }
          })}
          handleChange={(value) => {
            setSelectedFramework(value[0]);
          }}
          rootClassName={css.select}
          closeOnChange
        />
        {(essentialSkills.length > 0 || supportingSkills.length > 0) && (
          <div className={css.section}>
            {essentialSkills.length > 0 && (
              <Section label={t("skillsFrameworks:selectModal.essentialSkills")}>
                {essentialSkills.map(({ id, experience, customValue }) => (
                  <DoubleSectionPill
                    key={id}
                    leftContent={String(customValue.value)}
                    rightContent={getExperienceText(experience)}
                  />
                ))}
              </Section>
            )}
            {supportingSkills.length > 0 && (
              <Section label={t("skillsFrameworks:selectModal.supportingSkills")}>
                {supportingSkills.map(({ id, experience, customValue }) => (
                  <DoubleSectionPill
                    key={id}
                    leftContent={String(customValue.value)}
                    rightContent={getExperienceText(experience)}
                  />
                ))}
              </Section>
            )}
          </div>
        )}
        {customTypesWithValues.length > 0 && (
          <div className={css.section}>
            {customTypesWithValues.map(({ type, values }) => (
              <Section key={type.id} label={type.displayAs}>
                <CustomValuesList type={type} customValues={values} sort />
              </Section>
            ))}
          </div>
        )}
      </div>
    </ConfirmModal>
  );
};
